// What this device does with notices: show one as an OS notification, close
// them once read, set the app's badge, and go where a clicked one points.
//
// Promise:
// - Inside the desktop app the app shows notifications and the Dock badge (a
//   page its screen frames hands them to the app: `noticeDesktop`);
//   inside the iOS app the app keeps the icon's badge and takes away the
//   notifications of a chat read; elsewhere the browser's Notification API
//   and Badging API, when present.
//   Nothing here throws when an API is missing or refuses.
// - Going to a notice's screen does nothing when that screen is already open
//   (the router would load it again and scroll the chat back to its top).
import { SHELL_CONTRACT_VERSION } from "../shell/outside/contract";
import { appNoticeHost } from "./app-notices";
import {
  type DesktopShellNotice,
  desktopShell,
  noticeDesktop,
} from "./desktop-shell";

export type OsNotice = DesktopShellNotice;

export function notificationPermission(): NotificationPermission | null {
  return typeof Notification === "undefined" ? null : Notification.permission;
}

/** Shows a notice; a click focuses the window and calls `go` with its path. */
export function showOsNotice(notice: OsNotice, go: (url: string) => void) {
  const desktop = noticeDesktop();
  if (desktop) {
    desktop.notify(notice);
    return;
  }
  if (notificationPermission() !== "granted") return;
  try {
    const shown = new Notification(notice.title, {
      body: notice.body,
      tag: notice.tag,
    });
    shown.onclick = () => {
      window.focus();
      shown.close();
      go(notice.url);
    };
  } catch {
    // Some browsers only show notifications through a service worker.
  }
}

/** Closes this device's notifications with a tag (a chat that was read). */
export async function closeOsNotices(tag: string): Promise<void> {
  noticeDesktop()?.closeNotifications?.(tag);
  appNoticeHost()?.post({
    version: SHELL_CONTRACT_VERSION,
    type: "notices-clear",
    tag,
  });
  if (typeof navigator === "undefined" || !("serviceWorker" in navigator))
    return;
  try {
    const registration = await navigator.serviceWorker.getRegistration();
    for (const n of (await registration?.getNotifications({ tag })) ?? [])
      n.close();
  } catch {
    // Nothing to close.
  }
}

/** The app's badge (0 clears it). */
export function setAppBadge(count: number): void {
  const app = appNoticeHost();
  if (app) {
    app.post({ version: SHELL_CONTRACT_VERSION, type: "badge", count });
    return;
  }
  const desktop = desktopShell();
  if (desktop?.setBadge) {
    desktop.setBadge(count);
    return;
  }
  if (typeof navigator === "undefined" || !("setAppBadge" in navigator)) return;
  void (count > 0 ? navigator.setAppBadge(count) : navigator.clearAppBadge())
    // Not an installed app, or not allowed: there is no badge to set.
    .catch(() => {});
}

export const clearAppBadge = () => setAppBadge(0);

/** Whether `url` is the page open now (paths compared decoded, with the search). */
export function isOpenPage(
  url: string,
  current: { pathname: string; search: string },
): boolean {
  const to = new URL(url, "http://x");
  const decode = (p: string) => {
    try {
      return decodeURIComponent(p);
    } catch {
      return p;
    }
  };
  const search = (s: string) => (s === "" || s === "?" ? "" : s);
  return (
    decode(to.pathname) === decode(current.pathname) &&
    search(to.search) === search(current.search)
  );
}

export interface NoticeRouter {
  state: { location: { pathname: string; searchStr: string } };
  navigate(options: { href: string }): unknown;
}

/** Goes where a clicked notice points, unless it is the page open now. */
export function navigateFromNotice(router: NoticeRouter, url: string): void {
  // Read when the click happens (an event handler), not to draw anything.
  const { pathname, searchStr } = router.state.location;
  if (isOpenPage(url, { pathname, search: searchStr })) return;
  void router.navigate({ href: url });
}
